import { X, Edit, Trash2, Clock, Copy, Ban, Calendar, Send, Loader2, AlertTriangle } from 'lucide-react';
import { getSocialPostTimeStatus } from '../lib/socialPostStatusHelper';

interface SocialPostPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: {
    id: string;
    platform: string;
    post_content: string;
    status: string;
    scheduled_at: string | null;
    published_at: string | null;
    created_at: string;
    error_message: string | null;
    image_url: string | null;
    up_status?: string | null;
    retry_count?: number | null;
  };
  platformIcon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  platformColor: string;
  platformGradient: string;
  onEdit: () => void;
  onDelete: () => void;
  onSchedule: () => void;
  onCancelSchedule: () => void;
  onDuplicate: () => void;
  actionLoading: boolean;
}

export function SocialPostPreviewModal({
  isOpen,
  onClose,
  post,
  platformIcon: PlatformIcon,
  platformColor,
  platformGradient,
  onEdit,
  onDelete,
  onSchedule,
  onCancelSchedule,
  onDuplicate,
  actionLoading
}: SocialPostPreviewModalProps) {
  if (!isOpen) return null;

  function getStatusBadge(status: string) {
    switch (status) {
      case 'draft':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
            <Edit className="w-4 h-4 mr-1.5" />
            Concept
          </span>
        );
      case 'scheduled': {
        const timeStatus = getSocialPostTimeStatus(
          post.scheduled_at,
          post.status,
          post.up_status ?? null,
          post.retry_count ?? null
        );
        if (timeStatus.processingStatus === 'processing') {
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200">
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              {timeStatus.statusLabel}
            </span>
          );
        }
        if (timeStatus.processingStatus === 'delayed') {
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-50 text-amber-700 border border-amber-300">
              <Clock className="w-4 h-4 mr-1.5" />
              {timeStatus.statusLabel}
            </span>
          );
        }
        if (timeStatus.processingStatus === 'overdue') {
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 border border-red-300">
              <AlertTriangle className="w-4 h-4 mr-1.5" />
              {timeStatus.statusLabel}
            </span>
          );
        }
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            <Clock className="w-4 h-4 mr-1.5" />
            Gepland
          </span>
        );
      }
      case 'published':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <Send className="w-4 h-4 mr-1.5" />
            Gepubliceerd
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <X className="w-4 h-4 mr-1.5" />
            Mislukt
          </span>
        );
      default:
        return null;
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl bg-white md:rounded-2xl shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="sticky top-0 right-0 float-right m-4 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 transition-colors z-10"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>

        <div className="p-6 md:p-8">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white rounded-xl shadow-md border border-gray-100">
                  <PlatformIcon className="w-6 h-6" style={{ color: platformColor }} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#231F20]">
                    {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
                  </h2>
                  <p className="text-sm text-gray-500">Social media post</p>
                </div>
              </div>
              {getStatusBadge(post.status)}
            </div>
          </div>

          {post.image_url ? (
            <div className="mb-6 rounded-xl overflow-hidden shadow-lg">
              <img
                src={post.image_url}
                alt="Post afbeelding"
                className="w-full h-auto object-contain max-h-96"
              />
            </div>
          ) : (
            <div className={`mb-6 w-full aspect-video rounded-xl bg-gradient-to-br ${platformGradient} flex items-center justify-center shadow-lg`}>
              <PlatformIcon className="w-20 h-20 text-white opacity-40" />
            </div>
          )}

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Post tekst
            </h3>
            <div className="prose prose-sm max-w-none">
              <p className="text-base text-gray-800 leading-relaxed whitespace-pre-wrap">
                {post.post_content}
              </p>
            </div>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-xl space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Platform</span>
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium text-white"
                style={{ backgroundColor: platformColor }}
              >
                <PlatformIcon className="w-4 h-4" />
                {post.platform.charAt(0).toUpperCase() + post.platform.slice(1)}
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-600">Tekens</span>
              <span className="text-sm text-gray-800 font-medium">
                {post.post_content.length}
              </span>
            </div>

            {post.scheduled_at && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-600">Gepland voor</span>
                <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <Clock className="w-4 h-4 text-blue-600" />
                  {new Date(post.scheduled_at).toLocaleString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            )}

            {post.published_at && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <span className="text-sm font-medium text-gray-600">Verzonden op</span>
                <div className="flex items-center gap-2 text-sm text-gray-800 font-medium">
                  <Send className="w-4 h-4 text-green-600" />
                  {new Date(post.published_at).toLocaleString('nl-NL', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            )}
          </div>

          {post.error_message && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
              <h4 className="text-sm font-semibold text-red-900 mb-2">Foutmelding</h4>
              <p className="text-sm text-red-800">{post.error_message}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            {(post.status === 'draft' || post.status === 'failed') && (
              <>
                <button
                  onClick={onEdit}
                  className="flex-1 btn-primary inline-flex items-center justify-center gap-2"
                  disabled={actionLoading}
                >
                  <Edit className="w-4 h-4" />
                  Bewerken
                </button>
                <button
                  onClick={onSchedule}
                  className="flex-1 btn-secondary inline-flex items-center justify-center gap-2"
                  disabled={actionLoading}
                >
                  <Clock className="w-4 h-4" />
                  Inplannen
                </button>
              </>
            )}

            {post.status === 'scheduled' && (
              <>
                <button
                  onClick={onCancelSchedule}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-amber-500 text-amber-700 bg-amber-50 rounded-lg font-medium hover:bg-amber-100 transition-colors disabled:opacity-50"
                  disabled={actionLoading}
                >
                  <Ban className="w-4 h-4" />
                  {actionLoading ? 'Bezig...' : 'Annuleren'}
                </button>
                <button
                  onClick={onEdit}
                  className="flex-1 btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Bewerken
                </button>
              </>
            )}

            {post.status === 'published' && (
              <button
                onClick={onDuplicate}
                className="flex-1 btn-secondary inline-flex items-center justify-center gap-2"
                disabled={actionLoading}
              >
                <Copy className="w-4 h-4" />
                {actionLoading ? 'Bezig...' : 'Dupliceren'}
              </button>
            )}

            <button
              onClick={onDelete}
              className="sm:w-auto px-4 py-2.5 border-2 border-red-300 text-red-700 bg-white rounded-lg font-medium hover:bg-red-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              <span className="sm:inline">Verwijderen</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
